import { Check } from '@interhyp/platform-icons';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { LabelWithoutEventBubbling } from "../../helper/components/LabelWithoutEventBubbling.js";
import { LegacyIconRenderer } from "../../helper/components/LegacyIconRenderer.js";
import { useErrorProps } from "../../helper/hooks/useErrorProps.js";
import { useStableId } from "../../helper/hooks/useStableId.js";
import { getVariantClasses } from "../variantProvider/functions/getVariantClasses.js";
import styles from "./Checkbox.module.js";

// adding threshold of 16 to account for reacts scheduling within 1 requestAnimationFrame (1/60)
// without it, the animation _sometimes_ gets cut off
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const RIPPLE_DURATION = styles.RIPPLE_DURATION * 1000 + 16;
export const Checkbox = ({
  block,
  childrenErrorContainerClassName,
  error,
  classNames,
  children,
  inputRef,
  icon,
  isIntermediate = false,
  onClick,
  onFocus,
  onBlur,
  variant,
  id,
  ...props
}) => {
  const [focus, setFocus] = useState(false);
  const [animated, setAnimated] = useState(false);
  const classes = classnames(styles.checkbox, {
    [styles.checked]: props.checked,
    [styles.disabled]: props.disabled,
    [styles.error]: !!error,
    [styles.block]: block,
    [styles.focus]: focus,
    [styles.animated]: animated,
    [styles.withIcon]: icon !== undefined
  }, getVariantClasses(variant, !!error), classNames);
  const inputId = useStableId(id);
  const boxClasses = classnames(styles.box, {
    [styles.boxSecondBorder]: isIntermediate
  });
  function handleClick(event) {
    setAnimated(true);
    onClick?.(event);
  }
  function handleFocus(event) {
    setFocus(true);
    onFocus?.(event);
  }
  function handleBlur(event) {
    setFocus(false);
    onBlur?.(event);
  }
  useEffect(() => {
    if (animated) {
      const timeout = setTimeout(() => {
        setAnimated(false);
      }, RIPPLE_DURATION);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [animated]);
  const errorMessageClasses = classnames(styles.errorMessage, {
    [styles.block]: block
  });
  const {
    errorMessage,
    errorInputProps
  } = useErrorProps({
    error,
    identifier: inputId,
    className: errorMessageClasses
  });
  return /*#__PURE__*/_jsxs("div", {
    className: classes,
    children: [/*#__PURE__*/_jsx("input", {
      ref: inputRef,
      type: "checkbox",
      ...props,
      ...errorInputProps,
      id: inputId,
      className: classnames(styles.input, props.className),
      onClick: handleClick,
      onFocus: handleFocus,
      onBlur: handleBlur
    }), /*#__PURE__*/_jsx("div", {
      className: boxClasses,
      children: props.checked && /*#__PURE__*/_jsx(Check, {
        className: styles.checkIcon
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: childrenErrorContainerClassName,
      children: [/*#__PURE__*/_jsxs(LabelWithoutEventBubbling, {
        className: styles.label,
        htmlFor: inputId,
        "aria-readonly": props.readOnly,
        "aria-disabled": props.disabled,
        children: [icon !== undefined && /*#__PURE__*/_jsx(LegacyIconRenderer, {
          icon: icon,
          size: 16,
          className: styles.icon
        }), children]
      }), errorMessage]
    })]
  });
};